import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { Eye, History } from "lucide-react";
import api from "../api";
import { useAuth } from "../context/AuthContext";
import DataTable from "../components/DataTable";
import { PaymentHistoryDialog, SupplierPaymentDialog } from "../components/SupplierPayment";
import { Button } from "../components/ui/button";
import { fmtDate, fmtMoney } from "../utils/format";

const STATUS_STYLE = {
  "Soldée": "bg-emerald-100 text-emerald-700",
  "Partiellement payée": "bg-amber-100 text-amber-700",
  "En attente": "bg-red-100 text-red-700",
};

export default function SupplierDebts() {
  const [rows, setRows] = useState([]);
  const [payFor, setPayFor] = useState(null);
  const [histFor, setHistFor] = useState(null);
  const { user } = useAuth();
  const navigate = useNavigate();
  const canPay = ["admin", "comptable"].includes(user?.role);

  const load = () => api.get("/supplier-debts").then((r) => setRows(r.data));
  useEffect(() => { load(); }, []);

  const openCount = rows.filter((r) => r.remaining > 0).length;
  const totalRemaining = rows.reduce((s, r) => s + (r.remaining || 0), 0);

  return (
    <div className="space-y-5" data-testid="supplier-debts-page">
      <div className="flex flex-wrap items-center justify-between gap-3">
        <div>
          <h1 className="font-heading text-2xl font-bold tracking-tight text-slate-900">Dettes fournisseurs</h1>
          <p className="mt-1 text-sm text-slate-500">Suivi et règlement des dettes d'achats de véhicules.</p>
        </div>
        <div className="flex gap-3 text-center">
          <div className="rounded-lg bg-red-50 px-4 py-2" data-testid="debts-open-count">
            <p className="text-xs text-slate-500">Dettes ouvertes</p>
            <p className="font-mono-num font-bold text-red-600">{openCount}</p>
          </div>
          <div className="rounded-lg bg-red-50 px-4 py-2" data-testid="debts-total-remaining">
            <p className="text-xs text-slate-500">Reste total à payer</p>
            <p className="font-mono-num font-bold text-red-600">{fmtMoney(totalRemaining)}</p>
          </div>
        </div>
      </div>

      <DataTable testidPrefix="supplier-debts-table" rows={rows}
        searchKeys={["supplier_name", "purchase_number", "vehicle_label"]}
        columns={[
          { key: "supplier_name", label: "Fournisseur", render: (r) => <span className="font-semibold text-slate-800">{r.supplier_name || "—"}</span> },
          { key: "purchase_number", label: "Facture", render: (r) => (
            <div>
              <p className="font-mono-num font-semibold">{r.purchase_number}</p>
              {r.vehicle_label && <p className="text-xs text-slate-400">{r.vehicle_label}</p>}
            </div>
          ) },
          { key: "price_fcfa", label: "Montant total", className: "text-right", render: (r) => <span className="font-mono-num">{fmtMoney(r.price_fcfa)}</span> },
          { key: "paid_amount", label: "Déjà payé", className: "text-right", render: (r) => <span className="font-mono-num text-emerald-600">{fmtMoney(r.paid_amount)}</span> },
          { key: "remaining", label: "Reste à payer", className: "text-right", render: (r) => <span className={`font-mono-num font-semibold ${r.remaining > 0 ? "text-red-600" : "text-emerald-600"}`}>{fmtMoney(r.remaining)}</span> },
          { key: "due_date", label: "Échéance", render: (r) => (
            <span className={r.overdue ? "font-semibold text-red-600" : ""}>{fmtDate(r.due_date)}{r.overdue ? " · en retard" : ""}</span>
          ) },
          { key: "status", label: "Statut", render: (r) => (
            <span className={`rounded-full px-2 py-0.5 text-xs font-semibold ${STATUS_STYLE[r.status] || "bg-slate-100 text-slate-600"}`} data-testid={`debt-status-${r.purchase_number}`}>{r.status}</span>
          ) },
          { key: "actions", label: "", sortable: false, render: (r) => (
            <div className="flex items-center gap-1">
              <Button size="sm" variant="ghost" title="Voir le fournisseur" onClick={() => r.supplier_id && navigate(`/fournisseurs?open=${r.supplier_id}`)} data-testid={`view-debt-${r.purchase_number}`}><Eye className="h-4 w-4" /></Button>
              {r.remaining > 0 && canPay ? (
                <Button size="sm" data-testid={`pay-debt-${r.purchase_number}`} onClick={() => setPayFor(r)}>Payer</Button>
              ) : r.remaining <= 0 ? (
                <span className="px-2 text-xs font-semibold text-emerald-600" data-testid={`debt-soldee-${r.purchase_number}`}>Soldée</span>
              ) : null}
              <Button size="sm" variant="ghost" title="Historique des paiements" onClick={() => setHistFor(r)} data-testid={`history-debt-${r.purchase_number}`}><History className="h-4 w-4" /></Button>
            </div>
          ) },
        ]}
      />

      <SupplierPaymentDialog debt={payFor} onClose={() => setPayFor(null)} onPaid={load} />
      <PaymentHistoryDialog purchase={histFor} onClose={() => setHistFor(null)} />
    </div>
  );
}
